import { useTranslation } from "react-i18next";
import { CalendarX, Clock, User } from "lucide-react";

import { usePublicCancellation } from "../../application/usePublicCancellation";
import { formatBookingTime } from "../../domain/formatting";
import type { PublicCancellation } from "../../domain/types";

/**
 * Cancelling an appointment from the link in its email, with no account.
 *
 * The link is the authorisation, as a meeting link is — which is also why the
 * page says so little: when, how long and with whom, never the title or the
 * way in. A forwarded email should not hand anyone more than that.
 *
 * The button is the confirmation. Opening the link from an email and then
 * pressing a button that says exactly what it does is already two deliberate
 * steps; a third would only be friction for someone who cannot come.
 *
 * `translate="no"` for the reason the guest meeting page has it: a browser
 * translating the page rewrites the DOM under React, and the copy is already
 * in the patient's language.
 */
export default function PublicCancelAppointmentPage() {
    const { t, i18n } = useTranslation();
    const { state, submitting, refusal, retry, confirm } = usePublicCancellation();

    return (
        <main
            translate="no"
            className="flex min-h-screen items-center justify-center bg-surface-base px-4 py-10"
        >
            <div className="flex w-full max-w-md flex-col gap-5 rounded-2xl border border-border-subtle bg-surface-raised p-6 shadow-raised">
                <h1 className="flex items-center gap-2 text-xl font-semibold text-content-primary">
                    <CalendarX size={20} aria-hidden="true" />
                    {t("appointments.cancel.title")}
                </h1>

                {state.kind === "loading" && (
                    <p role="status" className="text-sm text-content-tertiary">
                        {t("appointments.cancel.loading")}
                    </p>
                )}

                {state.kind === "notFound" && (
                    <p role="alert" className="text-sm text-content-secondary">
                        {t("appointments.cancel.notFound")}
                    </p>
                )}

                {state.kind === "error" && (
                    <div className="flex flex-col items-start gap-3">
                        <p role="alert" className="text-sm text-content-secondary">
                            {t("appointments.cancel.loadError")}
                        </p>
                        <button
                            type="button"
                            onClick={retry}
                            className="min-h-11 rounded-xl border border-gray-300 px-4 text-sm font-medium dark:border-gray-700"
                        >
                            {t("appointments.cancel.retry")}
                        </button>
                    </div>
                )}

                {state.kind === "ready" && (
                    <ReadyView
                        cancellation={state.cancellation}
                        language={i18n.language}
                        submitting={submitting}
                        refusal={refusal}
                        onConfirm={confirm}
                    />
                )}
            </div>
        </main>
    );
}

function ReadyView({
    cancellation,
    language,
    submitting,
    refusal,
    onConfirm,
}: {
    cancellation: PublicCancellation;
    language: string;
    submitting: boolean;
    refusal: string | null;
    onConfirm: () => void;
}) {
    const { t } = useTranslation();
    const day = cancellation.date.slice(0, 10);
    const dayLabel = new Date(`${day}T12:00:00`).toLocaleDateString(language, {
        weekday: "long",
        day: "numeric",
        month: "long",
    });
    const deadline = new Date(cancellation.deadline).toLocaleString(language, {
        timeZone: cancellation.timezone,
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
    });
    const allowed = !cancellation.cancelled && cancellation.status === "allowed";

    return (
        <>
            <div className="flex flex-col gap-2 rounded-xl bg-surface-sunken p-4">
                <p className="text-lg font-semibold capitalize text-content-primary">{dayLabel}</p>
                <p className="flex items-center gap-1.5 text-sm text-content-secondary">
                    <Clock size={14} aria-hidden="true" className="shrink-0" />
                    {formatBookingTime(day, cancellation.hour, cancellation.timezone)} ·{" "}
                    {cancellation.duration} {t("scheduler.common.minutes")}
                </p>
                <p className="flex items-center gap-1.5 text-sm text-content-secondary">
                    <User size={14} aria-hidden="true" className="shrink-0" />
                    {t("appointments.cancel.with", { name: cancellation.therapist_name })}
                </p>
            </div>

            {cancellation.cancelled && (
                <p role="status" className="text-sm font-medium text-content-primary">
                    {t("appointments.cancel.done")}
                </p>
            )}

            {allowed && !refusal && (
                <>
                    <p className="text-sm text-content-tertiary">
                        {t("appointments.cancel.deadline", { deadline })}
                    </p>
                    <button
                        type="button"
                        onClick={onConfirm}
                        disabled={submitting}
                        aria-busy={submitting}
                        className="min-h-11 rounded-xl bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-60"
                    >
                        {t("appointments.cancel.confirm")}
                    </button>
                </>
            )}

            {!cancellation.cancelled && cancellation.status !== "allowed" && (
                <p className="text-sm text-content-secondary">
                    {t(`appointments.cancel.refused.${cancellation.status}`)}
                </p>
            )}

            {refusal && (
                <p role="alert" className="text-sm text-content-secondary">
                    {t(refusal)}
                </p>
            )}
        </>
    );
}
